// ==================== LIVE PIPELINE RUN STATUS ====================
// Renders a live banner whenever a lead search is running for this customer.
// All state lives in LeadGenRunContext, which is restored from the server on
// mount (so the banner survives a page refresh) and kept live via WebSocket
// run.started / run.progress / run.completed / run.failed events.

import { Loader2, CheckCircle2, Search, Sparkles, Target, AlertCircle } from 'lucide-react';
import { useLeadGenRun } from './LeadGenRunContext';

const STAGE_META: Record<string, { label: string; icon: React.ElementType }> = {
  discovery: { label: 'Discovering companies', icon: Search },
  enrichment: { label: 'Enriching & summarizing', icon: Sparkles },
  scoring: { label: 'Scoring against your ICP', icon: Target },
  outreach: { label: 'Drafting outreach', icon: Sparkles },
};

export default function LeadGenRunStatus() {
  const { run, activeRuns } = useLeadGenRun();

  // Several campaigns at once: one summary line (each card shows its own progress).
  if (activeRuns.length > 1) {
    const discovered = activeRuns.reduce((sum, r) => sum + (r.discovered ?? 0), 0);
    const places = [...new Set(activeRuns.map((r) => r.location).filter(Boolean))];
    return (
      <div className="flex items-center gap-3 rounded-lg border border-(--color-signal-orange)/30 bg-(--color-orange-glow) px-4 py-2.5 text-sm">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-(--color-signal-orange)" />
        <div className="flex-1 min-w-0">
          <span className="text-white font-medium">{activeRuns.length} campaigns running</span>
          <span className="text-(--color-muted-gray) ml-2">
            {places.length > 0 && <>· {places.slice(0, 3).join(', ')}{places.length > 3 ? ` +${places.length - 3}` : ''} </>}
            · {discovered} discovered so far
          </span>
        </div>
        <span className="text-[11px] uppercase tracking-wider text-(--color-signal-orange) shrink-0">Live</span>
      </div>
    );
  }

  if (!run) return null;

  if (run.failed) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-(--color-error)/30 bg-(--color-error-light) px-4 py-2.5 text-sm">
        <AlertCircle className="h-4 w-4 text-(--color-error) shrink-0" />
        <span className="text-white">
          Lead search failed{run.errorMessage ? <> — <span className="text-(--color-error)">{run.errorMessage}</span></> : '. Please try again.'}
        </span>
      </div>
    );
  }

  if (run.done) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-(--color-success)/30 bg-(--color-success)/10 px-4 py-2.5 text-sm">
        <CheckCircle2 className="h-4 w-4 text-(--color-success) shrink-0" />
        <span className="text-white">
          Lead search complete —{' '}
          <span className="font-semibold text-(--color-success)">{run.discovered ?? 0}</span> discovered
          {typeof run.totalDrafts === 'number' && run.totalDrafts > 0 && (
            <>, <span className="font-semibold text-(--color-success)">{run.totalDrafts}</span> drafts ready</>
          )}
        </span>
      </div>
    );
  }

  const meta = STAGE_META[run.stage] ?? STAGE_META.discovery;
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-(--color-signal-orange)/30 bg-(--color-orange-glow) px-4 py-2.5 text-sm">
      <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-(--color-signal-orange)" />
      </span>
      <div className="flex-1 min-w-0">
        <span className="text-white font-medium">
          <Icon className="inline h-3.5 w-3.5 mr-1.5 text-(--color-signal-orange) align-[-2px]" />
          {meta.label}
        </span>
        <span className="text-(--color-muted-gray) ml-2">
          {run.location && <>· {run.location} </>}
          {typeof run.discovered === 'number' && <>· {run.discovered} discovered </>}
          {typeof run.passed === 'number' && <>· {run.passed} qualified</>}
        </span>
      </div>
      <span className="text-[11px] uppercase tracking-wider text-(--color-signal-orange) shrink-0">Live</span>
    </div>
  );
}
